import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateWebhookInput,
  UpdateWebhookInput,
  WebhookDeliveryRecord,
  WebhookDetail,
  WebhookEventCode,
  WebhookListItem,
  WebhookStats,
  WebhookTestResult,
} from '@news-cms/shared';
import { ApiError, api } from './api';

export const webhooksListQueryOptions = queryOptions({
  queryKey: ['webhooks', 'list'] as const,
  queryFn: () => api<{ items: WebhookListItem[] }>('/api/admin/webhooks').then((d) => d.items),
});

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebhookInput) =>
      api<{ webhook: WebhookDetail }>('/api/admin/webhooks', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((d) => d.webhook),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksListQueryOptions.queryKey }),
  });
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWebhookInput }) =>
      api<{ webhook: WebhookListItem }>(`/api/admin/webhooks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }).then((d) => d.webhook),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksListQueryOptions.queryKey }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/admin/webhooks/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksListQueryOptions.queryKey }),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: ({ id, event }: { id: string; event?: WebhookEventCode }) =>
      api<{ result: WebhookTestResult }>(`/api/admin/webhooks/${id}/test`, {
        method: 'POST',
        body: JSON.stringify({ event }),
      }).then((d) => d.result),
  });
}

export function useWebhooks() {
  return useQuery(webhooksListQueryOptions);
}

export const webhookStatsQueryOptions = queryOptions({
  queryKey: ['webhooks', 'stats'] as const,
  queryFn: () => api<{ stats: WebhookStats }>('/api/admin/webhooks/stats').then((d) => d.stats),
  staleTime: 30_000,
});

export const webhookDeliveriesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['webhooks', 'deliveries', id] as const,
    queryFn: () =>
      api<{ items: WebhookDeliveryRecord[] }>(`/api/admin/webhooks/${id}/deliveries`).then(
        (d) => d.items,
      ),
    // Short stale time — admin opening this dialog wants recent results.
    staleTime: 10_000,
  });

/**
 * Mask the value of a `secret` query param in a webhook URL for DISPLAY only.
 * Way-A revalidate URLs embed the shared secret as `?secret=…`; showing it in
 * full in the list / dialogs leaks it to shoulder-surfers and screenshots.
 * Only touch display surfaces — the edit dialog keeps the raw URL so saving
 * doesn't drop the secret. Non-secret URLs pass through unchanged.
 */
export function maskUrlSecret(url: string): string {
  return url.replace(/([?&]secret=)[^&#]+/gi, '$1••••••');
}

export function webhookErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'WEBHOOK_NOT_FOUND':
      return 'ไม่พบ webhook นี้';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลที่กรอกไม่ถูกต้อง';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์จัดการ webhook (ต้องมี webhook.manage)';
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
